import { defineStore } from 'pinia';
import { ref } from 'vue';
import { db } from '../firebase/config';
import { useAuth } from '../firebase/auth';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  getDoc
} from 'firebase/firestore';
import { ref as storageRef, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { storage, functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { v4 as uuidv4 } from 'uuid'; // Add this import

// Add this helper function at the top of the file, before the store definition
const sanitizeData = (obj) => {
  const clean = {};
  Object.entries(obj).forEach(([key, value]) => {
    // Handle arrays
    if (Array.isArray(value)) {
      clean[key] = value.map(item => {
        if (typeof item === 'object' && item !== null) {
          return sanitizeData(item);
        }
        return item ?? null; // Convert undefined to null for array items
      });
    }
    // Handle objects
    else if (typeof value === 'object' && value !== null) {
      clean[key] = sanitizeData(value);
    }
    // Handle primitive values
    else if (value !== undefined) {
      clean[key] = value;
    }
    else {
      clean[key] = null; // Convert undefined to null
    }
  });
  return clean;
};

export const useStore = defineStore('main', () => {
  const { user } = useAuth();
  const checklists = ref([]);
  const completedChecklists = ref([]);
  const settings = ref({
    address: '',
    phone: ''
  });
  const pdfs = ref([]);
  const users = ref([]);

  const loadChecklists = async () => {
    try {
      if (!user.value) return;
      
      // Load checklists and their order
      const orderDoc = await getDoc(doc(db, 'settings', 'checklistOrder'));
      const orderData = orderDoc.exists() ? orderDoc.data() : { order: [] };
      const order = orderData.order || [];
      
      const querySnapshot = await getDocs(collection(db, 'checklists'));
      const loadedChecklists = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sections: doc.data().sections || [],
        items: doc.data().items || []
      }));

      // Sort checklists based on saved order
      if (order.length > 0) {
        loadedChecklists.sort((a, b) => {
          const indexA = order.indexOf(a.id);
          const indexB = order.indexOf(b.id);
          if (indexA === -1) return 1;  // Put new items at the end
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      }

      checklists.value = loadedChecklists;
    } catch (error) {
      console.error('Failed to load checklists:', error);
      checklists.value = []; // Ensure we always have an array
    }
  };

  const addChecklist = async (checklist) => {
    try {
      await setDoc(doc(db, 'checklists', checklist.id), checklist);
      checklists.value.push(checklist);
    } catch (error) {
      console.error('Failed to add checklist:', error);
    }
  };

  const removeChecklist = async (id) => {
    try {
      if (user.value.role !== 'viewer') {
        await deleteDoc(doc(db, 'checklists', id));
        checklists.value = checklists.value.filter(cl => cl.id !== id);
      } else {
        console.error('Permission denied: Viewers cannot delete checklists');
      }
    } catch (error) {
      console.error('Failed to remove checklist:', error);
    }
  };

  const updateChecklist = async (updatedChecklist) => {
    try {
      // Check if the checklist exists in Firestore
      const checklistRef = doc(db, 'checklists', updatedChecklist.id);
      const checklistSnap = await getDoc(checklistRef);

      if (checklistSnap.exists()) {
        // Update existing checklist
        await updateDoc(checklistRef, updatedChecklist);
      } else {
        // Create new checklist
        await setDoc(checklistRef, updatedChecklist);
      }

      // Update local state
      const index = checklists.value.findIndex(cl => cl.id === updatedChecklist.id);
      if (index !== -1) {
        checklists.value[index] = updatedChecklist;
      } else {
        checklists.value.push(updatedChecklist);
      }
    } catch (error) {
      console.error('Failed to update checklist:', error);
      throw error;
    }
  };

  const loadCompletedChecklists = async () => {
    try {
      if (!user.value) return;

      const userRoleDoc = await getDoc(doc(db, 'user_roles', user.value.uid));
      const userRole = userRoleDoc.exists() ? userRoleDoc.data().role : 'user';
      
      await loadChecklists();
      
      const completedRef = collection(db, 'completed_checklists');
      const querySnapshot = await getDocs(
        userRole === 'admin' 
          ? completedRef 
          : query(completedRef, where('userId', '==', user.value.uid))
      );
      
      // Get all user data first
      const userDocs = await Promise.all(
        [...new Set(querySnapshot.docs.map(doc => doc.data().userId))]
          .map(userId => getDoc(doc(db, 'user_roles', userId)))
      );
      const userMap = Object.fromEntries(
        userDocs
          .filter(doc => doc.exists())
          .map(doc => [doc.id, doc.data().name])
      );
      
      // Map checklists with user names from the map
      const checklists = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const template = getChecklistById(data.templateId);
        return {
          id: doc.id,
          ...data,
          title: template?.title || data.title || 'Untitled',
          sections: data.sections || [],
          items: data.items || [],
          completed_at: data.completed_at || new Date().toISOString(),
          userName: userMap[data.userId] || data.userName || 'Unknown user'
        };
      });

      checklists.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
      completedChecklists.value = checklists;
    } catch (error) {
      console.error('Failed to load completed checklists:', error);
      completedChecklists.value = [];
    }
  };

  const getNextCompletionNumber = async (templateNumber) => {
    try {
      // First check if we have a stored last number
      const completionRef = collection(db, 'completion_numbers');
      const trackerDoc = doc(completionRef, templateNumber);
      const trackerSnapshot = await getDoc(trackerDoc);

      let nextNumber;
      
      if (trackerSnapshot.exists()) {
        // If we have a stored number, just increment it by 1
        nextNumber = (trackerSnapshot.data().lastNumber || 0) + 1;
      } else {
        // If no stored number exists, check completed checklists
        const completedRef = collection(db, 'completed_checklists');
        const completedQuery = query(
          completedRef,
          where('templateNumber', '==', templateNumber)
        );
        const completedSnapshot = await getDocs(completedQuery);

        // Find the highest completion number
        let maxNumber = 0;
        completedSnapshot.forEach(doc => {
          const number = doc.data().completionNumber || 0;
          maxNumber = Math.max(maxNumber, number);
        });

        // Next number is highest + 1
        nextNumber = maxNumber + 1;
      }

      // Store the new number
      await setDoc(trackerDoc, {
        templateNumber,
        lastNumber: nextNumber,
        lastUpdated: new Date().toISOString()
      });

      return nextNumber;
    } catch (error) {
      console.error('Error getting next completion number:', error);
      throw error;
    }
  };

  const saveCompletedChecklist = async (checklistData) => {
    try {
      if (!user.value) throw new Error('User must be logged in');

      const templateId = checklistData.templateId || checklistData.template_id;
      const isNewChecklist = !checklistData.id;
      const docRef = isNewChecklist ? 
        doc(collection(db, 'completed_checklists')) : 
        doc(db, 'completed_checklists', checklistData.id);

      // Get template data - changed this part to search in raw checklists array
      const templateChecklist = checklists.value.find(cl => cl.id === templateId);
      if (!templateChecklist) {
        throw new Error('Template checklist not found');
      }

      // For new checklists, copy all items from template
      let sections = checklistData.sections;
      let items = checklistData.items;
      
      if (isNewChecklist) {
        // Deep copy sections from template
        sections = templateChecklist.sections?.map(section => ({
          ...section,
          subsections: section.subsections.map(subsection => ({
            ...subsection,
            items: subsection.items.map(item => ({
              ...item,
              status: '',
              comments: '',
              options: item.options.map(opt => ({
                ...opt,
                value: opt.type === 'input' ? '' : undefined
              }))
            }))
          }))
        }));

        // Deep copy items from template
        items = (templateChecklist.items || []).map(item => ({
          ...item,
          status: '',
          comments: '',
          options: item.options.map(opt => ({
            ...opt,
            value: opt.type === 'input' ? '' : undefined
          }))
        }));

        // Get next completion number for new checklist
        const completionNumber = await getNextCompletionNumber(templateChecklist.number);
        const combinedNumber = `${templateChecklist.number}-${completionNumber}`;

        // Prepare data for new checklist
        checklistData = {
          ...checklistData,
          id: docRef.id,
          templateId,
          sections,
          items,
          completionNumber,
          combinedNumber,
          templateNumber: templateChecklist.number
        };
      }

      // Prepare final data for saving
      const completedData = {
        ...checklistData,
        id: docRef.id,
        userId: user.value.uid,
        status: checklistData.status || 'in_progress',
        lastModified: new Date().toISOString()
      };

      // Save to Firestore
      await setDoc(docRef, sanitizeData(completedData), { merge: !isNewChecklist });

      // Update local state
      const savedData = { ...completedData };
      const existingIndex = completedChecklists.value.findIndex(cl => cl.id === docRef.id);
      
      if (existingIndex !== -1) {
        completedChecklists.value[existingIndex] = savedData;
      } else {
        completedChecklists.value.push(savedData);
      }

      return savedData;
    } catch (error) {
      console.error('Failed to save completed checklist:', error);
      throw error;
    }
  };

  const updateCompletedChecklist = async (updatedChecklist) => {
    try {
      await updateDoc(doc(db, 'completed_checklists', updatedChecklist.id), updatedChecklist);
      const index = completedChecklists.value.findIndex(cl => cl.id === updatedChecklist.id);
      if (index !== -1) {
        completedChecklists.value[index] = updatedChecklist;
      }
      return updatedChecklist;
    } catch (error) {
      console.error('Failed to update completed checklist:', error);
      throw error;
    }
  };

  const removeCompletedChecklist = async (id) => {
    try {
      // Get the checklist first to preserve completion number
      const checklistRef = doc(db, 'completed_checklists', id);
      const checklistSnap = await getDoc(checklistRef);
      
      if (checklistSnap.exists()) {
        // Delete the checklist
        await deleteDoc(checklistRef);
        
        // Update local state
        completedChecklists.value = completedChecklists.value.filter(cl => cl.id !== id);
        
        // Note: We don't delete from completion_numbers
        // This ensures the numbers remain sequential even after checklist deletion
      }
    } catch (error) {
      console.error('Failed to remove completed checklist:', error);
      throw error;
    }
  };

  const loadSettings = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'footer');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        settings.value = {
          address: data.address || '',
          phone: data.phone || ''
        };
      } else {
        settings.value = { address: '', phone: '' };
      }
      
      return settings.value;
    } catch (error) {
      console.error('Error loading settings:', error);
      settings.value = { address: '', phone: '' };
      throw error;
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const settingsRef = doc(db, 'settings', 'footer');
      await setDoc(settingsRef, newSettings);
      settings.value = newSettings;
    } catch (error) {
      console.error('Failed to update settings:', error);
      throw error;
    }
  };

  const uploadPDF = async (pdfFile, metadata) => {
    try {
      // Validate and sanitize metadata
      const sanitizedMetadata = {
        checklistId: metadata.checklistId || '',
        checklistTitle: metadata.checklistTitle || '',
        clientName: metadata.clientName || '',
        combinedNumber: metadata.combinedNumber || 'No Number'  // Provide default value
      };

      const timestamp = new Date().getTime();
      const fileName = `pdfs/${sanitizedMetadata.checklistId}/${timestamp}.pdf`;
      const fileRef = storageRef(storage, fileName);
      
      // Get user data from user_roles
      const userDoc = await getDoc(doc(db, 'user_roles', user.value.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      // Upload PDF to Firebase
      await uploadBytes(fileRef, pdfFile);
      const downloadURL = await getDownloadURL(fileRef);

      // Save PDF metadata to Firestore with sanitized data
      const pdfDoc = {
        fileName,
        downloadURL,
        createdAt: new Date().toISOString(),
        userId: user.value.uid,
        userName: userData.name || 'Unknown User',
        ...sanitizedMetadata // Use sanitized metadata
      };

      // Save PDF metadata to Firestore
      await setDoc(doc(db, 'pdfs', `${sanitizedMetadata.checklistId}-${timestamp}`), pdfDoc);

      // NocoDB upload in background
      const nocodbPayload = [{
        "Numero": sanitizedMetadata.combinedNumber,
        "Cliente (provisorio)": sanitizedMetadata.clientName,
        "Fecha": new Date().toISOString(),
        "PDF": [
          {
            "url": downloadURL,
            "title": `${sanitizedMetadata.combinedNumber}.pdf`,
            "mimetype": "application/pdf"
          }
        ],
        "Estado": "Pendiente",
      }];

      // Don't await this call, let it run in background
      const syncNocodb = httpsCallable(functions, 'syncNocodb');
      syncNocodb({
        env: process.env.NODE_ENV,
        data: nocodbPayload
      }).catch(error => {
        console.error('Failed to sync with NocoDB (non-blocking):', error);
      });

      return pdfDoc;
    } catch (error) {
      console.error('Error uploading PDF:', error);
      throw error;
    }
  };

  const loadPDFs = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'pdfs'));
      pdfs.value = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return pdfs.value;
    } catch (error) {
      console.error('Error loading PDFs:', error);
      throw error;
    }
  };

  // Add new function to send PDFs
  const sendPDFs = async (pdfs, emailTo) => {
    try {
      const sendPdfs = httpsCallable(functions, 'sendPdfs');
      const result = await sendPdfs({ pdfs, emailTo });
      return result.data;
    } catch (error) {
      console.error('Failed to send PDFs:', error);
      throw error;
    }
  };

  // Add new function to delete a PDF
  const deletePDF = async (pdfId) => {
    try {
      // Get the PDF document to get the fileName
      const pdfRef = doc(db, 'pdfs', pdfId);
      const pdfDoc = await getDoc(pdfRef);

      if (!pdfDoc.exists()) {
        throw new Error('PDF not found');
      }

      const pdfData = pdfDoc.data();

      // 1. Delete the file from Firebase Storage
      if (pdfData.fileName) {
        const fileRef = storageRef(storage, pdfData.fileName);
        await deleteObject(fileRef);
      }

      // 2. Delete the document from Firestore
      await deleteDoc(pdfRef);

      // 3. Update the local state
      pdfs.value = pdfs.value.filter(pdf => pdf.id !== pdfId);

      return true;
    } catch (error) {
      console.error('Error deleting PDF:', error);
      throw error;
    }
  };

  // Getter methods
  const getChecklistById = (id) => checklists.value.find(cl => cl.id === id);
  const getCompletedChecklistById = (id) => completedChecklists.value.find(cl => cl.id === id);

  // Move initialization to a separate function
  const initializeStore = () => {
    loadChecklists();
    loadCompletedChecklists();
    loadSettings();
  };

  // Call initialize on store creation
  initializeStore();

  const updateChecklistStatus = async (checklistId, status) => {
    try {
      const docRef = doc(db, 'completed_checklists', checklistId);
      await updateDoc(docRef, {
        status,
        lastModified: new Date().toISOString()
      });
      
      // Update local state
      const index = completedChecklists.value.findIndex(cl => cl.id === checklistId);
      if (index !== -1) {
        completedChecklists.value[index] = {
          ...completedChecklists.value[index],
          status,
          lastModified: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('Failed to update checklist status:', error);
      throw error;
    }
  };

  const archiveChecklist = async (checklistId) => {
    try {
      const docRef = doc(db, 'completed_checklists', checklistId);
      await updateDoc(docRef, {
        archived: true,
        archivedAt: new Date().toISOString()
      });
      
      // Update local state
      const index = completedChecklists.value.findIndex(cl => cl.id === checklistId);
      if (index !== -1) {
        completedChecklists.value[index] = {
          ...completedChecklists.value[index],
          archived: true,
          archivedAt: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('Failed to archive checklist:', error);
      throw error;
    }
  };

  const unarchiveChecklist = async (checklistId) => {
    try {
      const docRef = doc(db, 'completed_checklists', checklistId);
      await updateDoc(docRef, {
        archived: false,
        archivedAt: null
      });
      
      // Update local state
      const index = completedChecklists.value.findIndex(cl => cl.id === checklistId);
      if (index !== -1) {
        completedChecklists.value[index] = {
          ...completedChecklists.value[index],
          archived: false,
          archivedAt: null
        };
      }
    } catch (error) {
      console.error('Failed to unarchive checklist:', error);
      throw error;
    }
  };

  // Add this new function to load users
  const loadUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'user_roles'));
      users.value = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return users.value;
    } catch (error) {
      console.error('Error loading users:', error);
      throw error;
    }
  };

  // Add this new function to update checklist user
  const updateChecklistUser = async (checklistId, newUserId) => {
    try {
      // Get new user data
      const userDoc = await getDoc(doc(db, 'user_roles', newUserId));
      if (!userDoc.exists()) throw new Error('User not found');
      const userData = userDoc.data();

      const docRef = doc(db, 'completed_checklists', checklistId);
      await updateDoc(docRef, {
        userId: newUserId,
        userName: userData.name,
        lastModified: new Date().toISOString()
      });
      
      // Update local state
      const index = completedChecklists.value.findIndex(cl => cl.id === checklistId);
      if (index !== -1) {
        completedChecklists.value[index] = {
          ...completedChecklists.value[index],
          userId: newUserId,
          userName: userData.name,
          lastModified: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('Failed to update checklist user:', error);
      throw error;
    }
  };

  const saveInspectorSignature = async (signatureData) => {
    try {
      if (!user.value) throw new Error('User must be logged in');
      
      const userRef = doc(db, 'user_roles', user.value.uid);
      await updateDoc(userRef, {
        signature: signatureData,
        signatureUpdatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to save inspector signature:', error);
      throw error;
    }
  };

  const getInspectorSignature = async () => {
    try {
      if (!user.value) return null;
      
      const userDoc = await getDoc(doc(db, 'user_roles', user.value.uid));
      return userDoc.exists() ? userDoc.data().signature : null;
    } catch (error) {
      console.error('Failed to get inspector signature:', error);
      return null;
    }
  };

  const duplicateChecklist = async (checklistId) => {
    try {
      const original = checklists.value.find(cl => cl.id === checklistId);
      if (!original) throw new Error('Checklist not found');

      // Create new ID and increment rev for duplicate
      const newId = uuidv4();
      const newRev = 1;

      // Create duplicate without existing ID and with new rev
      const duplicate = {
        ...original,
        id: newId,
        rev: newRev,
        title: `${original.title} (Copy)`,
        number: `${original.number}-COPY`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Save to Firestore
      await setDoc(doc(db, 'checklists', newId), duplicate);
      
      // Update local state
      checklists.value.push(duplicate);
      
      return duplicate;
    } catch (error) {
      console.error('Failed to duplicate checklist:', error);
      throw error;
    }
  };

  return {
    checklists,
    completedChecklists,
    loadChecklists,
    addChecklist,
    removeChecklist,
    updateChecklist,
    getChecklistById,
    loadCompletedChecklists,
    saveCompletedChecklist,
    getCompletedChecklistById,
    updateCompletedChecklist,
    removeCompletedChecklist,
    getNextCompletionNumber,
    settings,
    loadSettings,
    updateSettings,
    pdfs,
    uploadPDF,
    loadPDFs,
    deletePDF, // Make sure this is included in the return object
    updateChecklistStatus,
    archiveChecklist,
    unarchiveChecklist,
    users,
    loadUsers,
    updateChecklistUser,
    saveInspectorSignature,
    getInspectorSignature,
    sendPDFs,
    duplicateChecklist,
  };
});